import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatLongDate } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Bus, ArrowLeft, ScrollText, Mail, Scale, Lock, FileCode } from 'lucide-react';

const LIST = (items) => (
  <ul className="list-disc pl-6 space-y-1 mt-2 text-foreground/90">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const H = ({ children }) => (
  <h3 className="text-lg font-semibold mt-5 mb-2 text-foreground">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-foreground/90 leading-relaxed">{children}</p>
);

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50" data-testid="terms-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-sm bg-white flex-shrink-0">
              <img src="/ntwanayam-logo.jpg" alt="NtwanaYam Logo" className="w-full h-full object-cover" />
            </div>
            <span className="font-bold text-lg text-foreground">NtwanaYam</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full hidden sm:inline-flex" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Link to="/login">
              <Button variant="outline" className="rounded-full" data-testid="login-btn">Sign In</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 py-10 sm:py-12 text-center max-w-4xl">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium mb-5" data-testid="hero-badge">
          <ScrollText className="w-4 h-4" /> Legal
        </div>
        <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight mb-4" data-testid="hero-title">
          Terms &amp; Legal
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
          Please read these documents carefully. By using NtwanaYam you agree to the terms below.
        </p>
        <p className="text-sm text-muted-foreground mt-3" data-testid="last-updated">
          Last updated: {formatLongDate(new Date())}
        </p>
      </section>

      {/* Content */}
      <section className="container mx-auto px-4 pb-16 max-w-4xl">
        <Card className="shadow-lg border-emerald-100">
          <CardContent className="p-4 sm:p-8">
            <Accordion type="multiple" defaultValue={['copyright']} className="w-full">

              {/* Copyright */}
              <AccordionItem value="copyright" data-testid="section-copyright">
                <AccordionTrigger className="text-left">
                  <div className="flex items-center gap-3">
                    <Scale className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-base sm:text-lg font-semibold">Copyright Notice</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm sm:text-base">
                  <P><strong>Copyright © 2026 NtwanaYam. All Rights Reserved.</strong></P>
                  <P>
                    NtwanaYam is a digital platform designed to facilitate learner transport management,
                    communication between parents, transport providers, schools, and related stakeholders.
                  </P>
                  <P>
                    All content contained within the NtwanaYam platform, including but not limited to software code,
                    source code, databases, website content, mobile application content, graphics, designs, logos,
                    trademarks, icons, text, documents, images, videos, reports, and other intellectual property,
                    is the exclusive property of NtwanaYam and its licensors.
                  </P>
                  <P>
                    No part of the NtwanaYam platform may be copied, reproduced, modified, distributed, published,
                    transmitted, sold, licensed, reverse-engineered, or exploited for commercial purposes without
                    prior written consent from NtwanaYam.
                  </P>
                  <P>
                    Unauthorized access, duplication, modification, or distribution of NtwanaYam intellectual
                    property may result in civil and criminal penalties under applicable South African
                    intellectual property laws.
                  </P>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-4">
                    <p className="font-medium text-foreground mb-1">For copyright enquiries, please contact:</p>
                    <p className="text-sm text-foreground/90">NtwanaYam</p>
                    <p className="text-sm text-foreground/90 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-emerald-600" />
                      <a href="mailto:info@ntwanayam.co.za" className="text-primary hover:underline">info@ntwanayam.co.za</a>
                    </p>
                    <p className="text-sm text-foreground/90">Website: www.ntwanayam.co.za</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Terms & Conditions */}
              <AccordionItem value="terms" data-testid="section-terms">
                <AccordionTrigger className="text-left">
                  <div className="flex items-center gap-3">
                    <ScrollText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-base sm:text-lg font-semibold">Terms and Conditions</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm sm:text-base">
                  <H>1. Acceptance of Terms</H>
                  <P>By accessing or using NtwanaYam, you agree to be bound by these Terms and Conditions.</P>
                  <P>If you do not agree with these terms, you must discontinue use of the platform immediately.</P>

                  <H>2. Definitions</H>
                  <P><strong>&quot;Platform&quot;</strong> means the NtwanaYam website, mobile applications, software systems, and associated services.</P>
                  <P><strong>&quot;User&quot;</strong> means any parent, guardian, learner, driver, transport provider, school representative, or administrator using the platform.</P>
                  <P><strong>&quot;Services&quot;</strong> means all functionalities provided by NtwanaYam.</P>

                  <H>3. User Accounts</H>
                  <P>Users are responsible for:</P>
                  {LIST([
                    'Maintaining the confidentiality of login credentials.',
                    'Ensuring information provided is accurate and up to date.',
                    'Reporting unauthorized access immediately.',
                  ])}
                  <P className="mt-2">NtwanaYam reserves the right to suspend or terminate accounts that violate these terms.</P>

                  <H>4. Acceptable Use</H>
                  <P>Users shall not:</P>
                  {LIST([
                    'Use the platform for unlawful purposes.',
                    'Upload harmful or malicious software.',
                    'Attempt unauthorized access to any system.',
                    'Interfere with platform operations.',
                    'Share false or misleading information.',
                  ])}

                  <H>5. Learner Transport Information</H>
                  <P>NtwanaYam provides technology to facilitate communication and management of learner transport services.</P>
                  <P>NtwanaYam does not operate transport vehicles unless expressly stated.</P>
                  <P>Transport providers remain responsible for:</P>
                  {LIST([
                    'Vehicle roadworthiness.',
                    'Driver licensing.',
                    'Insurance.',
                    'Compliance with applicable laws.',
                  ])}

                  <H>6. Payments</H>
                  <P>Where paid services are offered:</P>
                  {LIST([
                    'Fees must be paid as specified.',
                    'Failure to pay may result in service suspension.',
                    'Fees are non-refundable unless otherwise stated.',
                  ])}

                  <H>7. Intellectual Property</H>
                  <P>All rights, title, and interest in the platform remain the property of NtwanaYam.</P>
                  <P>Users receive a limited, non-transferable license to access and use the platform.</P>

                  <H>8. Limitation of Liability</H>
                  <P>NtwanaYam shall not be liable for:</P>
                  {LIST([
                    'Transport delays.',
                    'Accidents.',
                    'Network outages.',
                    'Data transmission failures.',
                    'Indirect or consequential damages.',
                  ])}

                  <H>9. Termination</H>
                  <P>NtwanaYam may suspend or terminate access where users:</P>
                  {LIST([
                    'Breach these terms.',
                    'Misuse the platform.',
                    'Engage in fraudulent activities.',
                  ])}

                  <H>10. Governing Law</H>
                  <P>These Terms and Conditions shall be governed by the laws of the Republic of South Africa.</P>
                </AccordionContent>
              </AccordionItem>

              {/* Privacy Policy */}
              <AccordionItem value="privacy" data-testid="section-privacy">
                <AccordionTrigger className="text-left">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <span className="text-base sm:text-lg font-semibold">Privacy Policy</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm sm:text-base">
                  <H>Introduction</H>
                  <P>
                    NtwanaYam respects your privacy and is committed to protecting personal information in
                    accordance with the Protection of Personal Information Act (POPIA).
                  </P>

                  <H>Information We Collect</H>
                  <P>We may collect:</P>
                  {LIST([
                    'Names and surnames.',
                    'Contact information.',
                    'Identification details.',
                    'Learner information.',
                    'School information.',
                    'Driver information.',
                    'Vehicle information.',
                    'Payment information.',
                    'Device and usage information.',
                  ])}

                  <H>Purpose of Collection</H>
                  <P>Information is collected to:</P>
                  {LIST([
                    'Provide transport management services.',
                    'Facilitate communication.',
                    'Improve user experience.',
                    'Ensure platform security.',
                    'Meet legal obligations.',
                  ])}

                  <H>Sharing of Information</H>
                  <P>Information may be shared with:</P>
                  {LIST([
                    'Authorized schools.',
                    'Authorized transport providers.',
                    'Service providers supporting the platform.',
                    'Regulatory authorities when legally required.',
                  ])}
                  <P className="mt-2"><strong>NtwanaYam does not sell personal information.</strong></P>

                  <H>Data Security</H>
                  <P>
                    NtwanaYam implements reasonable technical and organizational measures to protect personal
                    information against unauthorized access, loss, misuse, or disclosure.
                  </P>

                  <H>Data Retention</H>
                  <P>
                    Information will be retained only for as long as necessary to provide services or comply
                    with legal obligations.
                  </P>

                  <H>User Rights</H>
                  <P>Users may:</P>
                  {LIST([
                    'Access their information.',
                    'Request corrections.',
                    'Request deletion where legally permissible.',
                    'Object to certain processing activities.',
                  ])}

                  <H>Children&apos;s Information</H>
                  <P>
                    Where learner information is processed, parental or guardian consent may be required in
                    accordance with applicable laws.
                  </P>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                    <p className="font-medium text-foreground mb-1">Privacy Officer</p>
                    <p className="text-sm text-foreground/90 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-purple-600" />
                      <a href="mailto:legal@ntwanayam.co.za" className="text-primary hover:underline">legal@ntwanayam.co.za</a>
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* EULA */}
              <AccordionItem value="eula" data-testid="section-eula">
                <AccordionTrigger className="text-left">
                  <div className="flex items-center gap-3">
                    <FileCode className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="text-base sm:text-lg font-semibold">Software License Agreement (EULA)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm sm:text-base">
                  <H>License Grant</H>
                  <P>
                    NtwanaYam grants users a limited, revocable, non-exclusive, non-transferable license to
                    use the software for its intended purposes.
                  </P>

                  <H>Ownership</H>
                  <P>The software remains the exclusive property of NtwanaYam.</P>
                  <P>This agreement does not transfer ownership rights to the user.</P>

                  <H>Restrictions</H>
                  <P>Users may not:</P>
                  {LIST([
                    'Reverse engineer the software.',
                    'Modify source code.',
                    'Decompile software.',
                    'Create derivative works.',
                    'Resell software access.',
                    'Rent or sublicense the platform.',
                  ])}

                  <H>Updates</H>
                  <P>NtwanaYam may provide software updates, enhancements, bug fixes, and security patches.</P>

                  <H>Service Availability</H>
                  <P>
                    NtwanaYam strives for continuous service availability but does not guarantee uninterrupted
                    operation.
                  </P>

                  <H>Termination</H>
                  <P>Violation of this agreement automatically terminates the license granted.</P>

                  <H>Disclaimer</H>
                  <P>
                    The software is provided on an &quot;as available&quot; basis without warranties of uninterrupted service.
                  </P>

                  <H>Limitation of Liability</H>
                  <P>
                    NtwanaYam&apos;s total liability shall be limited to the amount paid by the user for the
                    services during the preceding twelve months.
                  </P>

                  <H>Governing Law</H>
                  <P>This agreement shall be governed by the laws of South Africa.</P>

                  <p className="text-center text-sm text-muted-foreground italic mt-6">— End of Agreement —</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-emerald-100 bg-white/50 py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground space-y-1">
          <p>
            Contact:{' '}
            <a href="mailto:info@ntwanayam.co.za" className="text-primary hover:underline" data-testid="footer-email">
              info@ntwanayam.co.za
            </a>
          </p>
          <p>© {new Date().getFullYear()} NtwanaYam. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
